/**
 * NP-Flow AI (K7): the shapes of `GET/PUT /api/ai/settings`,
 * `/api/ai/skills/*` and `/api/ai/prompts` (apps/app/src/routes/ai.ts).
 */
import type { SegmentFilter } from './api';

export type AiLanguage = 'en' | 'da' | 'de' | 'fr' | 'it' | 'nl';

export const AI_LANGUAGE_LIST: readonly { value: AiLanguage; label: string }[] = [
  { value: 'en', label: 'English' },
  { value: 'da', label: 'Danish' },
  { value: 'de', label: 'German' },
  { value: 'fr', label: 'French' },
  { value: 'it', label: 'Italian' },
  { value: 'nl', label: 'Dutch' },
];

/**
 * What the admin tells the assistant about the company; added to every prompt.
 * The brand profile fields beyond these come from the questionnaire the API
 * sends (`AiBrandProfileResponse.questions`), so the form needs no field list.
 */
export interface AiBrand {
  tone: string;
  doWords: string[];
  dontWords: string[];
  products: string[];
  languages: AiLanguage[];
  signOff: string;
  rules: string[];
}

export interface AiBrandProduct {
  name: string;
  what: string;
  facts: string;
  never: string;
}

export interface AiBrandChoice {
  value: string;
  label: string;
}

/** One question of the brand questionnaire (`GET /api/ai/brand-profile`). */
export interface AiBrandQuestion {
  id: string;
  section: string;
  question: string;
  help: string;
  type: 'text' | 'list' | 'choice' | 'products';
  maxLength?: number;
  maxItems?: number;
  options?: readonly AiBrandChoice[];
  multiple?: boolean;
  countsInProgress: boolean;
}

export type AiBrandValue = string | string[] | AiBrandProduct[];

export interface AiBrandProfileResponse {
  sections: { id: string; title: string }[];
  questions: AiBrandQuestion[];
  profile: Record<string, AiBrandValue>;
  progress: { answered: number; total: number };
  /** The brand block as Markdown, ready to paste into Claude. */
  promptText: string;
}

export interface AiSettings {
  enabled: boolean;
  dailyRequestCap: number;
  brand: AiBrand;
  updatedAt: number | null;
}

export interface AiSettingsResponse extends AiSettings {
  /** False when this environment has no Workers AI binding. */
  available: boolean;
  usage: {
    day: string;
    requests: number;
    neurons: number;
    neuronBudget: number;
    freeNeuronsPerDay: number;
  };
}

export interface AiPrompt {
  id: string;
  title: string;
  body: string;
  shared: boolean;
  createdBy: string;
  createdAt: number;
  updatedAt: number;
  /** True when the caller wrote it (only the author or an admin may change it). */
  mine: boolean;
}

export interface AiSubjectSuggestion {
  language: AiLanguage;
  subject: string;
  preheader: string;
}

export interface AiNewsletterResult {
  template: { id: string; name: string };
  subject: string;
  preheader: string;
  /** The sent mail whose layout and tone were followed; null = plain layout (nothing sent yet). */
  reference: { id: string; name: string } | null;
  /** Texts the assistant left as they were in that mail. */
  keptTexts: number;
}

/** A sent campaign the newsletter draft can take its layout and tone from. */
export interface AiNewsletterReference {
  id: string;
  name: string;
  sentAt: number | null;
  /** From the mail's own lang attribute; '' when it has none. */
  language: string;
}

export interface AiTranslateResult {
  created: { language: AiLanguage; template: { id: string; name: string } }[];
  failed: { language: AiLanguage; message: string }[];
}

export interface AiSegmentSuggestion {
  name: string;
  filter: SegmentFilter;
}

/** K7 scheduled AI tasks: `/api/ai/tasks` (apps/app/src/routes/aiTasks.ts). */
export type AiTaskKind = 'prompt' | 'library_prompt' | 'subject_lines' | 'newsletter' | 'segment';

export type AiTaskInputs =
  | { kind: 'prompt'; text: string }
  | { kind: 'library_prompt'; promptId: string }
  | { kind: 'subject_lines'; topic: string; languages: AiLanguage[]; count: number }
  | { kind: 'newsletter'; bullets: string; language: AiLanguage }
  | { kind: 'segment'; description: string };

export interface AiTaskInput {
  name: string;
  frequency: 'daily' | 'weekly';
  /** 0 = Sunday ... 6 = Saturday (UTC); null for daily. */
  weekday: number | null;
  /** 0-23, UTC. */
  hour: number;
  input: AiTaskInputs;
}

export type AiTaskStatus = 'ok' | 'error' | 'skipped';

export interface AiTask extends AiTaskInput {
  id: string;
  kind: AiTaskKind;
  enabled: boolean;
  nextDueAt: number | null;
  lastRunAt: number | null;
  lastStatus: AiTaskStatus | null;
  lastError: string | null;
  createdBy: string;
  createdAt: number;
  updatedBy: string;
  updatedAt: number;
  /** "Every Monday at 09:00 UTC". */
  scheduleLabel: string;
}

export interface AiTasksResponse {
  tasks: AiTask[];
  aiEnabled: boolean;
  available: boolean;
  usage: { day: string; runs: number; cap: number };
}

export interface AiTaskRun {
  id: string;
  taskId: string;
  slotAt: number | null;
  source: 'schedule' | 'manual';
  status: 'running' | AiTaskStatus;
  startedAt: number;
  finishedAt: number | null;
  /** Depends on the kind: {text}, {suggestions}, {template, subject, preheader} or {name, filter}. */
  output: unknown;
  templateId: string | null;
  error: string | null;
}
